import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import { CROSS_SECTION_QUERY_KEY } from 'components/cross-section/use-cross-section';
import {
  Button,
  Checkbox,
  IconButton,
  TextInput,
  InlineNotification,
  InlineLoading,
} from '@carbon/react';
import {
  Renew,
  Restart,
  CheckmarkFilled,
  Settings,
  Maximize,
  Minimize,
} from '@carbon/react/icons';
import { formatMutationResource } from 'utils/misc';
import type {
  PreDrillWorkspace,
  PreDrillStructureGridProfilesResponse,
  PreDrillGunBarrelResponse,
  Profile2D,
} from 'types/api-responses';
import type { ColorByRule } from 'components/color-by-attribute-modal';
import {
  preDrillStructureGridProfilesAtom,
  preDrillGunBarrelAtom,
  preDrillWellProfileAtom,
  preDrillColorByUwiMapAtom,
} from './atoms';
import {
  useGeometryDepthControls,
  type GeometryDepthControls,
  type GeometryRefreshOptions,
} from './use-geometry-depth-controls';
import { colorForBoreUwi, buildFormationBands } from './utils';
import { PreDrillProfile2DChart } from './pre-drill-profile-2d-chart';
import { PreDrillGunBarrelChart } from './pre-drill-gun-barrel-chart';
import { WorkspaceCrossSection } from './workspace-cross-section';
import styles from './subsurface-explorer.module.scss';

interface SubsurfaceExplorerProps {
  workspace: PreDrillWorkspace;
  /** All color-by rules; the charts paint by the rules whose Apply-To includes
   *  their surface (gunBarrel / profile2d), resolved the same way as the Map. */
  colorRules: ColorByRule[];
  /** Set when geometry_construction failed — shows the reason as a banner while
   *  the previous geometry stays on the charts. */
  geometryError: { code?: string; message?: string } | null;
  /** Called after a geometry refresh is enqueued so the parent re-fetches the
   *  show — picking up the now-processing status, which arms the Pusher. */
  onRefreshEnqueued: () => void;
  /** True while a target-zone re-run is in flight or its Pusher job is still
   *  generating — blocks Recalculate Geometry until the target zone settles. */
  targetZoneBusy: boolean;
  /** True from the moment Recalculate is enqueued until its Pusher push lands —
   *  keeps the button disabled through the run, regardless of the show status. */
  geometryRefreshing: boolean;
  /** Whether any point carries a depth, which settles whether the bore is depthless
   *  when the geometry status cannot (UAT 37). */
  hasPointDepths?: boolean;
  /** Permitted vertical depth — the default the Target TVD field pre-fills when
   *  generalizing (no grid) with no committed manual depth. Null if none. */
  permittedDepthTvdFt: number | null;
  /** Story 7: the depth controls when the Wellbore Points panel hosts them; the
   *  header then renders none of its own. Omitted, the Explorer keeps its own. */
  depthControls?: GeometryDepthControls;
  /**
   * Opens the structure grid collection picker. A host with no settings sidebar (the
   * geometry tearsheet) hands this in so the collection stays reachable (UAT 16);
   * omitted, the header shows no gear and the sidebar owns it as before.
   */
  onOpenGridCollection?: () => void;
  /**
   * Hosted beside the geometry map (UAT 10): the right pane is the main map's
   * cross-section along the map's A–A′ line instead of the gun barrel. The prod
   * workspace, which has no such map, keeps the gun barrel.
   */
  crossSectionAlongMap?: boolean;
  /** The Well Workspace the sheet is open under; its edits join the cross-section. */
  wellWorkspaceId?: number | null;
  /**
   * Hosted in the column beside the map (INS-12936): the two depth views stack in that one
   * column, so the plan view and both depth views read together. No divider: each view takes
   * the column's full width. Omitted, they sit side by side as before.
   */
  stacked?: boolean;
  /** Reports expand / shrink of a stacked view, so the host can give it the whole row as it
   *  does an expanded map (INS-12936). */
  onExpandedChange?: (expanded: boolean) => void;
  /** Reports whether a reverted geometry is available, for a host's Revert control. */
  onCanRevertChange?: (canRevert: boolean) => void;
  /**
   * Story 8: show a depthless wellbore's views behind a banner instead of gating them
   * on a confirmed target zone. False on a backend that serves neither the points nor
   * the intersected grids, where the gate is still the only honest thing to show.
   */
  ungateDepthless?: boolean;
}

// Imperative handle: parent calls `refreshCharts()` to re-pull the charts after
// a geometry event (same id can rebuild, so a value-keyed effect won't re-fire).
export interface SubsurfaceExplorerHandle {
  refreshCharts: () => void;
  /** Snapshot the charts for Revert, then enqueue the depth derivation. */
  recalculate: (options?: GeometryRefreshOptions) => void;
  /** Pin the pre-derivation snapshot back onto the charts. */
  revert: () => void;
}

const MIN_LEFT_PCT = 25;
const MAX_LEFT_PCT = 75;

type ProfileResult = PreDrillStructureGridProfilesResponse['results'][number];
// The gun-barrel response is the result itself (no `results` envelope).
type GunBarrelResult = PreDrillGunBarrelResponse;
// Snapshot of both charts' data so "Revert to previous geometry" can roll back
// to the state shown before the last Recalculate.
interface GeometrySnapshot {
  profile: ProfileResult | null;
  gunBarrel: GunBarrelResult | null;
}

// Subsurface Explorer: fetches the 2D Profile + Gun Barrel and renders them
// side-by-side on a shared TVDSS domain with a draggable divider.
export const SubsurfaceExplorer = forwardRef<
  SubsurfaceExplorerHandle,
  SubsurfaceExplorerProps
>((props, ref) => {
  const {
    workspace,
    colorRules,
    geometryError,
    onRefreshEnqueued,
    targetZoneBusy,
    geometryRefreshing,
    hasPointDepths = false,
    permittedDepthTvdFt,
    depthControls: hostedDepthControls,
    onOpenGridCollection,
    crossSectionAlongMap = false,
    wellWorkspaceId = null,
    onCanRevertChange,
    ungateDepthless = false,
    stacked = false,
    onExpandedChange,
  } = props;
  const sliceMd = workspace.state.gunBarrelSliceMd ?? 8500;
  const collectionId = workspace.structureGridCollectionId;
  // Built geometry row id, the subject for both chart queries; null until
  // geometry_construction lands (querying before then would 400).
  const geometryId = workspace.preDrillWellSurveyGeometryId;
  const geometryReady = workspace.geometryStatus === 'processed';
  // Geometry is only built AFTER a zone is confirmed. On a fresh workspace the
  // row is a `pending` stub, so `pending` alone must NOT read as constructing.
  const hasConfirmedZone = workspace.targetZoneGeologicIntervalId !== null;
  // Geometry is being (re)built: `processing`, or a derive still awaited. The pre-drill
  // page also reads a confirmed zone on a `pending` stub as a build on its way; the sheet
  // doesn't, since its bore carries its elected geometry's zone from the start.
  const geometryConstructing =
    !geometryError &&
    (ungateDepthless
      ? workspace.geometryStatus === 'processing' || geometryRefreshing
      : // The pre-drill page keeps its own rule: only a confirmed zone builds.
        hasConfirmedZone &&
        (workspace.geometryStatus === 'pending' ||
          workspace.geometryStatus === 'processing'));
  // Nothing built, running or failed: the sheet says so and draws what it can. A bore
  // with a depth on any point (a filed survey on `pending`) is never depthless (UAT 37).
  const depthless =
    !geometryError &&
    !geometryReady &&
    !geometryConstructing &&
    !hasPointDepths;
  // Neutral pre-selection state: no confirmed zone and nothing built yet — show
  // a prompt instead of a perpetual spinner or empty charts.
  const awaitingTargetZone =
    !geometryError &&
    !geometryReady &&
    !geometryConstructing &&
    !hasConfirmedZone;

  const [profilesResource, fetchProfiles] = useAtom(
    preDrillStructureGridProfilesAtom
  );
  const [gunBarrelResource, fetchGunBarrel] = useAtom(preDrillGunBarrelAtom);
  const [wellProfileResource, fetchWellProfile] = useAtom(
    preDrillWellProfileAtom
  );
  // Grid vs generalized depth: one hook shared with the Wellbore Points panel so the two
  // never disagree. Hooks always run; the host's controls win when given.
  const ownDepthControls = useGeometryDepthControls({
    workspace,
    permittedDepthTvdFt,
    onRefreshEnqueued,
  });
  const depth = hostedDepthControls ?? ownDepthControls;
  const {
    gridDataActive,
    hasUsableGrid,
    tvdInput,
    tvdNum,
    tvdOutOfRange,
    depthValid,
    depthDirty,
    recalcSubmitted,
    refreshEnabled,
  } = depth;
  const noGeologicReference = workspace.noGeologicReference;

  const [leftPct, setLeftPct] = useState(50);
  // Stacked beside the map, either view can be expanded to the whole row, as the map can: at
  // half the column's height neither chart is readable (UAT, Dustin).
  const [expandedView, setExpandedView] = useState<'profile' | 'right' | null>(
    null
  );
  const splitRowRef = useRef<HTMLDivElement | null>(null);

  // revertSnapshot: geometry before the last Recalculate (enables Revert).
  // pinnedSnapshot: snapshot pinned after a Revert (overrides live results).
  const [revertSnapshot, setRevertSnapshot] = useState<GeometrySnapshot | null>(
    null
  );
  const [pinnedSnapshot, setPinnedSnapshot] = useState<GeometrySnapshot | null>(
    null
  );

  const fetchCharts = useCallback(() => {
    const collectionIds = collectionId ? [collectionId] : undefined;
    if (geometryId === null) {
      // The sheet opens a surveyed bore that has no geometry row yet (a pilot hole, or
      // a drilled bore nobody has edited); its profile is taken off the filed survey by
      // the bore's address. The Gun Barrel is not in the sheet, so nothing else is asked.
      if (!crossSectionAlongMap) return;
      fetchProfiles({
        type: 'POST',
        url: '/queries/structure_grid_profiles',
        data: {
          query: {
            subject_kind: 'well',
            uwi14s: [workspace.uwi14],
            request_type: 'landing_analysis',
            search_radius_ft: 5280,
            collection_ids: collectionIds,
          },
        },
      });
      return;
    }
    // Only query once the geometry is built (a stub row 404s); the sheet asks as soon as
    // the row exists (UAT 23), where an unbuilt bore is served by its filed survey.
    if (!geometryReady && !ungateDepthless) return;
    // 2D Profile — flat subject query keyed by the geometry row id.
    fetchProfiles({
      type: 'POST',
      url: '/queries/structure_grid_profiles',
      data: {
        query: {
          subject_kind: 'pre_drill_well_survey_geometry',
          pre_drill_well_survey_geometry_id: geometryId,
          request_type: 'landing_analysis',
          search_radius_ft: 5280,
          collection_ids: collectionIds,
        },
      },
    });
    // Gun Barrel — subject + poi_layers envelope (same geometry row id).
    fetchGunBarrel({
      type: 'POST',
      url: '/queries/gun_barrel',
      data: {
        query: {
          subject: {
            kind: 'pre_drill_well_survey_geometry',
            pre_drill_well_survey_geometry_id: geometryId,
            eval_point_md_ft: sliceMd,
          },
          poi_layers: [
            { kind: 'wells', search_radius_ft: 5280 },
            {
              kind: 'structure_grid_formations',
              collection_ids: collectionIds,
            },
          ],
        },
      },
    });
  }, [
    geometryId,
    geometryReady,
    ungateDepthless,
    collectionId,
    sliceMd,
    crossSectionAlongMap,
    workspace.uwi14,
    fetchProfiles,
    fetchGunBarrel,
  ]);

  // Load charts on mount + whenever geometry id / collection / slice change
  // (no-op until a geometry row exists). Parent forces re-pulls via the handle.
  useEffect(() => {
    fetchCharts();
  }, [fetchCharts]);

  // What the parent calls after every write. The cross-section is invalidated too: an
  // edit moves the draft crossing under an unchanged cut key.
  const queryClient = useQueryClient();
  const refreshCharts = useCallback(() => {
    fetchCharts();
    queryClient.invalidateQueries({ queryKey: [CROSS_SECTION_QUERY_KEY] });
  }, [fetchCharts, queryClient]);

  // Subject well's pre-drill profile2D (surface→toe VS vs TVDSS) from
  // /queries/wells, keyed on the 10-digit UWI (the source_uwi match).
  const subjectUwi10 = workspace.uwi14.slice(0, 10);
  const subjectUwi14 = workspace.uwi14;
  const workspaceId = workspace.id;
  useEffect(() => {
    fetchWellProfile({
      type: 'POST',
      url: '/queries/wells',
      data: {
        source_uwi: subjectUwi10,
        radius: '100',
        includeProfile2d: true,
        includePreDrill: true,
        // The sheet can be opened on a pilot hole, which only comes back when asked for.
        ...(crossSectionAlongMap ? { includePilotHoles: true } : {}),
        // Drive the subject's profile2D from this workspace's geometry, once it exists.
        ...(workspaceId === null
          ? {}
          : {
              wellboreGeometryWorkspaceSelections: {
                [subjectUwi14]: workspaceId,
              },
            }),
      },
    });
  }, [
    subjectUwi10,
    subjectUwi14,
    workspaceId,
    crossSectionAlongMap,
    fetchWellProfile,
  ]);

  const { loading: profilesLoading, data: profilesData } =
    formatMutationResource(profilesResource);
  const { loading: gunBarrelLoading, data: gunBarrelData } =
    formatMutationResource(gunBarrelResource);
  const { loading: wellProfileLoading, data: wellProfileData } =
    formatMutationResource(wellProfileResource);

  // Source well's profile2D off the wells response — the wellbore matching this
  // workspace's uwi14, so the line reflects exactly this workspace's geometry.
  const wellProfile2D: Profile2D | null = useMemo(() => {
    const wells = wellProfileData?.results ?? [];
    const source = wells.find((w) => w.uwi === subjectUwi10) ?? wells[0];
    return (
      source?.wellBores?.find((b) => b.uwi14 === subjectUwi14)?.profile2D ??
      null
    );
  }, [wellProfileData, subjectUwi10, subjectUwi14]);
  // Pinned snapshot (after a Revert) wins over the live query results.
  const profileResult = pinnedSnapshot
    ? pinnedSnapshot.profile
    : (profilesData?.results?.[0] ?? null);
  const gunBarrelResult = pinnedSnapshot
    ? pinnedSnapshot.gunBarrel
    : (gunBarrelData ?? null);
  const loading = !pinnedSnapshot && (profilesLoading || gunBarrelLoading);

  // Shared TVDSS domain across both charts, memoized on the plotted data alone
  // so a control toggle keeps the same reference and doesn't re-draw the charts.
  const tvdssDomain: [number, number] | null = useMemo(() => {
    const tvdssValues: number[] = [];
    const bands = [
      ...(profileResult ? buildFormationBands(profileResult.formations) : []),
      ...(gunBarrelResult?.formationHorizontals
        ? buildFormationBands(gunBarrelResult.formationHorizontals.formations)
        : []),
    ];
    bands.forEach((b) => tvdssValues.push(b.topTvdss, b.bottomTvdss));
    profileResult?.wellboreTrajectory?.tvdssValues.forEach((v) =>
      tvdssValues.push(v)
    );
    // The profile2D well line (surface→toe) so its full extent is in view.
    wellProfile2D?.trajectory.forEach(([, tvdss]) => tvdssValues.push(tvdss));
    gunBarrelResult?.poiLayers
      ?.find((layer) => layer.kind === 'wells')
      ?.wells.forEach((w) => tvdssValues.push(w.tvdssFt));
    // Subject bore isn't in the wells layer — fold its eval-point TVDSS in.
    gunBarrelResult?.subject?.evalPoints?.forEach((e) =>
      tvdssValues.push(e.tvdssFt)
    );
    if (tvdssValues.length === 0) return null;
    const min = Math.min(...tvdssValues);
    const max = Math.max(...tvdssValues);
    const pad = Math.max(100, (max - min) * 0.1);
    return [min - pad, max + pad];
  }, [profileResult, gunBarrelResult, wellProfile2D]);

  /**
   * Regenerates the geometry, keeping the current one for Revert.
   * @param {GeometryRefreshOptions} [options] What to regenerate.
   */
  const handleRefresh = (options?: GeometryRefreshOptions) => {
    // Snapshot the current geometry for Revert, then enqueue the regen. onSuccess
    // re-fetches the show (arms the Pusher); the completion push re-pulls charts.
    setRevertSnapshot({ profile: profileResult, gunBarrel: gunBarrelResult });
    setPinnedSnapshot(null);
    depth.refresh(options);
  };

  // Revert: pin the pre-Recalculate snapshot back onto the charts and clear the
  // stash (disabling the button until the next recalc). Only armable when the
  // stash holds actual data — a Recalculate fired before the charts resolved
  // would stash an empty snapshot, and reverting to blank charts is meaningless.
  const canRevert =
    revertSnapshot !== null &&
    (revertSnapshot.profile !== null || revertSnapshot.gunBarrel !== null);
  /** Pins the snapshot taken before the last refresh, putting the old geometry back on screen. */
  const handleRevert = () => {
    if (!revertSnapshot) return;
    setPinnedSnapshot(revertSnapshot);
    setRevertSnapshot(null);
  };
  useEffect(() => {
    onCanRevertChange?.(canRevert);
  }, [canRevert, onCanRevertChange]);

  // The parent's handle: re-pull the charts after a geometry event, and Story 7's
  // Wellbore Points panel drives Update Wellbore Depths and Revert Geometry through it.
  useImperativeHandle(
    ref,
    () => ({
      refreshCharts,
      recalculate: handleRefresh,
      revert: handleRevert,
    }),
    // `handleRefresh` and `handleRevert` are rebuilt every render, so the handle keys on
    // the values they close over instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refreshCharts, revertSnapshot, profileResult, gunBarrelResult, depth]
  );
  const onDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    /**
     * Resizes the split to follow the pointer, clamped to the usable range.
     * @param {MouseEvent} ev The pointer move.
     */
    const onMove = (ev: MouseEvent) => {
      const row = splitRowRef.current;
      if (!row) return;
      const rect = row.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setLeftPct(Math.max(MIN_LEFT_PCT, Math.min(MAX_LEFT_PCT, pct)));
    };
    /** Ends the drag and unhooks its window listeners. */
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  const aligned =
    geometryReady &&
    !workspace.geometryStale &&
    workspace.targetZoneStatus === 'confirmed';

  // Chart color-by, same resolution as the Map but scoped per surface. The 2D
  // Profile paints only the subject stick by its UWI on the profile2d surface.
  const colorByUwiMap = useAtomValue(preDrillColorByUwiMapAtom);
  const subjectUwi = workspace.uwi14;
  const profileSubjectColor = colorForBoreUwi(
    subjectUwi,
    subjectUwi,
    colorRules,
    colorByUwiMap,
    'profile2d'
  );

  const rightTitle = crossSectionAlongMap ? 'Cross-Section' : 'Gun Barrel';
  const rightSubtitle = crossSectionAlongMap
    ? "Along the map's A–A′ line."
    : 'Based on mid point of pre-drill geometry.';
  const profileChart = (
    <PreDrillProfile2DChart
      result={profileResult}
      wellProfile2D={wellProfile2D}
      tvdssDomain={tvdssDomain}
      isLoading={loading || wellProfileLoading}
      subjectColor={profileSubjectColor}
      // Only the tearsheet, beside its Cross-Section: every other host keeps
      // today's depth-only zoom.
      sectionControls={crossSectionAlongMap}
    />
  );
  const rightChart = crossSectionAlongMap ? (
    <WorkspaceCrossSection
      collectionId={collectionId}
      wellWorkspaceId={wellWorkspaceId}
      uwi14={workspace.uwi14}
      colorRules={colorRules}
    />
  ) : (
    <PreDrillGunBarrelChart
      result={gunBarrelResult}
      tvdssDomain={tvdssDomain}
      isLoading={loading}
      colorRules={colorRules}
      colorByUwiMap={colorByUwiMap}
      subjectUwi={subjectUwi}
    />
  );

  /**
   * Expand / shrink, the map's own control. The other view hides rather than unmounts, so its
   * chart keeps its state through the expand.
   * @param {'profile' | 'right'} view Which view the button expands.
   * @param {string} name Its label, for the tooltip and the accessible name.
   * @return {JSX.Element} The button.
   */
  const expandButton = (view: 'profile' | 'right', name: string) => {
    const expanded = expandedView === view;
    return (
      <IconButton
        kind="ghost"
        size="sm"
        align="left"
        label={expanded ? `Shrink ${name}` : `Expand ${name}`}
        onClick={(event) => {
          setExpandedView(expanded ? null : view);
          onExpandedChange?.(!expanded);
          if (!expanded) {
            event.currentTarget
              .closest('section')
              ?.scrollIntoView({ block: 'start' });
          }
        }}
      >
        {expanded ? <Minimize size={16} /> : <Maximize size={16} />}
      </IconButton>
    );
  };

  /**
   * Stacked beside the map, each view gets the column's full width under its own title;
   * otherwise they sit side by side with a draggable divider.
   * @return {JSX.Element} The two views.
   */
  const renderViews = () => {
    if (stacked) {
      /**
       * @param {'profile' | 'right'} view Which view's canvas.
       * @return {string} Its class, expanded or not.
       */
      const canvasClass = (view: 'profile' | 'right') =>
        classNames(styles.stackCanvas, {
          [styles.stackCanvasExpanded]: expandedView === view,
        });
      return (
        <div
          className={styles.stack}
          data-expanded={expandedView !== null || undefined}
        >
          <div className={styles.stackPane} hidden={expandedView === 'right'}>
            <div className={styles.stackTitleRow}>
              <span className={styles.paneTitle}>2D Profile</span>
              {expandButton('profile', '2D Profile')}
            </div>
            <div className={canvasClass('profile')}>{profileChart}</div>
          </div>
          <div className={styles.stackPane} hidden={expandedView === 'profile'}>
            <div className={styles.stackTitleRow}>
              <span className={styles.paneTitle}>
                {rightTitle}
                <span className={styles.paneSubtitle}>{rightSubtitle}</span>
              </span>
              {expandButton('right', rightTitle)}
            </div>
            <div className={canvasClass('right')}>{rightChart}</div>
          </div>
        </div>
      );
    }
    /* Expanded, the chosen view takes the whole row and the other steps aside — the same
       trade the stacked arrangement makes, and what the map's own expand does to this
       column. The divider goes with it: there is nothing left to resize against. */
    /**
     * @param {'profile' | 'right'} view Which view.
     * @return {boolean} Whether the OTHER view's expand hides it.
     */
    const splitHidden = (view: 'profile' | 'right') =>
      expandedView !== null && expandedView !== view;
    /**
     * @param {'profile' | 'right'} view Which view.
     * @return {string} Its width — the full row when expanded, else its share of the split.
     */
    const splitWidth = (view: 'profile' | 'right') => {
      if (expandedView !== null) return '100%';

      return `${view === 'profile' ? leftPct : 100 - leftPct}%`;
    };

    return (
      <>
        <div className={styles.titleRow}>
          <span
            className={styles.splitTitleCell}
            style={{ width: splitWidth('profile') }}
            hidden={splitHidden('profile')}
          >
            <span className={styles.paneTitle}>2D Profile</span>
            {expandButton('profile', '2D Profile')}
          </span>
          <span
            className={styles.titleDividerSpacer}
            aria-hidden="true"
            hidden={expandedView !== null}
          />
          <span
            className={styles.splitTitleCell}
            style={{ width: splitWidth('right') }}
            hidden={splitHidden('right')}
          >
            <span className={styles.paneTitle}>
              {rightTitle}
              <span className={styles.paneSubtitle}>{rightSubtitle}</span>
            </span>
            {expandButton('right', rightTitle)}
          </span>
        </div>

        <div className={styles.splitRow} ref={splitRowRef}>
          <div
            className={styles.pane}
            style={{ width: splitWidth('profile') }}
            hidden={splitHidden('profile')}
          >
            <div className={styles.canvas}>{profileChart}</div>
          </div>

          <div
            className={styles.dragDivider}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize subsurface explorer columns"
            onMouseDown={onDividerMouseDown}
            hidden={expandedView !== null}
          >
            <span className={styles.dragDots} aria-hidden="true">
              ⠿
            </span>
          </div>

          <div
            className={styles.pane}
            style={{ width: splitWidth('right') }}
            hidden={splitHidden('right')}
          >
            <div className={styles.canvas}>{rightChart}</div>
          </div>
        </div>
      </>
    );
  };

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <h2
          className={classNames(styles.title, {
            [styles.titleHosted]: Boolean(hostedDepthControls),
          })}
        >
          Subsurface Explorer
        </h2>
        <div className={styles.headerActions}>
          {onOpenGridCollection && (
            <IconButton
              kind="ghost"
              size="sm"
              align="left"
              label="Structure grid collection settings"
              onClick={onOpenGridCollection}
            >
              <Settings size={16} />
            </IconButton>
          )}
          {/* Grid / generalized-depth controls in the top-right header row
              (mock parity), kept out of the body so they survive Constructing.
              A host that renders them itself (Story 7's Wellbore Points panel)
              takes the whole row, Recalculate and Revert included. */}
          {!hostedDepthControls && (
            <>
              <div className={styles.depthInputs}>
                <Checkbox
                  id="pre-drill-use-grid-data"
                  labelText="Use Grid Data"
                  className={styles.permittedDepthCheckbox}
                  checked={gridDataActive}
                  disabled={!refreshEnabled || !hasUsableGrid}
                  onChange={(_evt, { checked }) =>
                    depth.setUseGridData(checked)
                  }
                />
                <TextInput
                  id="pre-drill-generalized-tvd"
                  labelText="Target TVD (ft)"
                  placeholder="0–25,000"
                  inputMode="numeric"
                  className={styles.generalizedTvdInput}
                  value={gridDataActive ? '' : tvdInput}
                  disabled={!refreshEnabled || gridDataActive}
                  invalid={tvdOutOfRange}
                  invalidText="Target TVD must be between 0 and 25,000 ft."
                  onChange={(e) => depth.setTvdInput(e.target.value)}
                />
              </div>
              <Button
                kind="tertiary"
                size="sm"
                hasIconOnly
                renderIcon={Restart}
                iconDescription="Revert to previous geometry"
                tooltipPosition="bottom"
                disabled={!canRevert}
                onClick={handleRevert}
              />
              <Button
                kind="tertiary"
                size="sm"
                renderIcon={Renew}
                disabled={
                  // Enabled when geometry is stale or a depth input changed; else
                  // disabled through the regen, re-runs, bad TVD, and post-click.
                  !refreshEnabled ||
                  (!workspace.geometryStale && !depthDirty) ||
                  !depthValid ||
                  depth.refreshing ||
                  geometryRefreshing ||
                  geometryConstructing ||
                  targetZoneBusy ||
                  recalcSubmitted
                }
                onClick={() => handleRefresh()}
              >
                Recalculate Geometry
              </Button>
            </>
          )}
        </div>
      </div>

      <div className={styles.body}>
        {geometryError && (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title="Geometry construction failed"
            subtitle={
              geometryError.message ||
              'The pre-drill wellbore geometry could not be constructed. The previous geometry is shown below — adjust the inputs and retry Recalculate Geometry.'
            }
          />
        )}
        {workspace.geometryStale &&
          geometryReady &&
          pinnedSnapshot === null && (
            <InlineNotification
              kind="warning"
              lowContrast
              hideCloseButton
              title="Pre-drill target zone has changed"
              subtitle="Recalculate the wellbore geometry to reflect the updated selection."
            />
          )}
        {/* Depth / method staged but not applied — prompt a recalc. Hidden once
            Recalculate is clicked or the target-zone banner is showing. */}
        {depthDirty &&
          !recalcSubmitted &&
          geometryReady &&
          !workspace.geometryStale &&
          pinnedSnapshot === null && (
            <InlineNotification
              kind="warning"
              lowContrast
              hideCloseButton
              title="Depth settings changed"
              subtitle="Recalculate Geometry to apply the updated depth to the charts."
            />
          )}
        {/* Reverted view is frontend-cache only: the charts show a snapshot from
            before the last Recalculate, which the backend won't promote (Promote
            freezes the geometry built for the confirmed target zone). Warn so the
            user knows what's on screen isn't what gets promoted. */}
        {pinnedSnapshot !== null && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            title="Reverted geometry will not be promoted"
            subtitle="You reverted to a previous geometry. This is a frontend-only view — Promote uses the latest recalculated geometry for the confirmed target zone, not what is shown here."
          />
        )}
        {aligned && pinnedSnapshot === null && (
          <div className={styles.alignedBanner}>
            <CheckmarkFilled className={styles.alignedBannerIcon} />
            <span>
              <strong>
                Pre-Drill Wellbore Geometry Aligned to Target Zone
              </strong>
            </span>
          </div>
        )}

        {/* No-geologic-reference banner. The generalized-depth inputs
            themselves live in the top-right header row (see .headerActions).
            Suppressed while a reverted snapshot is pinned — it describes the
            current geometry, not the older snapshot on the charts. */}
        {noGeologicReference && pinnedSnapshot === null && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            title="No geologic reference"
            subtitle={
              tvdNum === null
                ? 'The selected target zone has no structure grid at the well. Geometry is generalized through the permitted vertical depth.'
                : 'The selected target zone has no structure grid at the well. Geometry is generalized through the entered Target TVD.'
            }
          />
        )}

        {/* Story 8: no gate on a confirmed zone. A depthless bore says so and the
            views draw what they can; the banner clears once depths are derived. A warning
            per the INS-12936 banner table; the remedy is the one the interpretation card
            gives (Target Zone or Permitted TVD), approved over the table's Wellbore Points
            wording so the two same-titled banners agree. */}
        {depthless && ungateDepthless && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            title="This wellbore geometry has no associated depths."
            subtitle="Recalculate the geometry from a Target Zone selection, or from the Permitted TVD, to derive them."
          />
        )}
        {awaitingTargetZone && !ungateDepthless ? (
          <div className={styles.awaiting}>
            Select a target zone to construct the pre-drill wellbore geometry.
          </div>
        ) : geometryConstructing ? (
          <div className={styles.constructing}>
            <InlineLoading description="Constructing wellbore geometry…" />
          </div>
        ) : (
          renderViews()
        )}
      </div>
    </section>
  );
});

SubsurfaceExplorer.displayName = 'SubsurfaceExplorer';
